import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BadgeCheck, MapPin, EyeOff } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, eur, errMsg, fmtDate } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Loader, Stars, Chip, Section, Empty, InsuranceBadge } from "@/components/common";
import { ReportButton } from "@/components/ReportDialog";

const DAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function ProviderMiniMap({ approx, name }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const map = L.map(el, { scrollWheelZoom: true }).setView([approx.lat, approx.lon], 14);
    L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}", { maxZoom: 19, attribution: 'Karte: Esri, HERE, Garmin, &copy; OpenStreetMap-Mitwirkende' }).addTo(map);
    L.circle([approx.lat, approx.lon], { radius: 900, color: "#047857", fillColor: "#19e6b3", fillOpacity: 0.4, weight: 2 }).addTo(map).bindPopup(`Ungefähre Lage von ${name}`);
    const id = setTimeout(() => map.invalidateSize(), 120);
    return () => { clearTimeout(id); map.remove(); };
  }, [approx, name]);
  return <div ref={ref} className="rounded-[20px] overflow-hidden border border-line z-0" style={{ height: 260 }} data-testid="provider-map" />;
}

function ServicesInfo({ p }) {
  const s = p.services || {};
  const at = p.attributes || {};
  const offered = [["dryer", "Trockner"], ["folding", "Falten"], ["ironing", "Bügeln"], ["pickup", "Abholung"], ["return", "Rücklieferung"], ["softener", "Weichspüler"], ["perfume", "Wäscheparfum"], ["sensitive", "Sensitiv-Waschmittel"]].filter(([k]) => s[k]);
  const flags = [["smoke_free", "Rauchfreier Haushalt"], ["pet_free", "Haustierfreier Haushalt"], ["eco_detergent", "Öko-/Bio-Waschmittel"], ["allergy_friendly", "Allergikerfreundlich"], ["steam_ironing", "Dampfbügeln"], ["express", "Express (24 h)"], ["flexible_times", "Flexible Zeiten"]].filter(([k]) => at[k]);
  return (
    <div className="space-y-4" data-testid="provider-price-list">
      <div className="rounded-2xl bg-mint-soft border border-mint p-4 flex items-center justify-between gap-3">
        <span className="font-semibold">Waschen (Einheitspreis)</span><span className="font-extrabold text-mint-ink tabular-nums">{eur(p.price_per_kg || 2.99)} / kg</span>
      </div>
      <div><p className="w-label">Angebotene Zusatzleistungen</p>
        <div className="flex flex-wrap gap-2">{offered.length ? offered.map(([k, l]) => <Chip key={k} tone="mint">{l}</Chip>) : <span className="w-muted text-sm">Nur Waschen</span>}</div>
      </div>
      {flags.length > 0 && <div><p className="w-label">Besonderheiten</p><div className="flex flex-wrap gap-2">{flags.map(([k, l]) => <Chip key={k} tone="info">{l}</Chip>)}</div></div>}
      {(at.detergent || at.perfume_brand || at.machines) && (
        <ul className="text-sm space-y-1 pt-3 border-t border-line">
          {at.detergent && <li><span className="w-muted">Waschmittel:</span> <span className="font-semibold">{at.detergent}</span></li>}
          {at.perfume_brand && <li><span className="w-muted">Wäscheparfum:</span> <span className="font-semibold">{at.perfume_brand}</span></li>}
          {at.machines && <li><span className="w-muted">Geräte & Ausstattung:</span> <span className="font-semibold">{at.machines}</span></li>}
        </ul>
      )}
    </div>
  );
}

export default function ProviderPage() {
  const { id } = useParams();
  const { t, lang } = useT();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/providers/${id}`).then((r) => setP(r.data)).catch((e) => setError(errMsg(e)));
  }, [id]);

  if (error) return <div className="w-container py-10"><Empty>{error}</Empty></div>;
  if (!p) return <Loader />;
  return (
    <div className="w-container py-8 grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 space-y-6">
        <div className="w-card p-6 flex flex-col sm:flex-row gap-5 w-rise">
          {p.avatar_url
            ? <img src={`${process.env.REACT_APP_BACKEND_URL}${p.avatar_url}`} alt={p.display_name} className="h-20 w-20 rounded-full object-cover shrink-0 border border-line" data-testid="provider-avatar" />
            : <span className="h-20 w-20 rounded-full bg-mint-soft text-mint-ink grid place-items-center font-extrabold text-3xl shrink-0" aria-hidden="true">{p.display_name[0]}</span>}
          <div className="space-y-2">
            <h1 className="text-3xl font-extrabold" data-testid="provider-name">{p.display_name}</h1>
            <div className="flex flex-wrap gap-2 items-center">
              <Stars value={p.rating} count={p.review_count} />
              {p.verified && <Chip tone="mint"><BadgeCheck size={14} aria-hidden="true" /> Identität geprüft</Chip>}
              <Chip tone="info" testId="provider-status-chip">{p.acts_as === "unternehmer" ? "Gewerblicher Anbieter (Unternehmer)" : "Privater Anbieter"}</Chip>
              {p.tier !== "standard" && <Chip tone="info">{p.tier.toUpperCase()}</Chip>}
            </div>
            <div className="mt-2" data-testid="provider-insurance-badge"><InsuranceBadge insurance={p.insurance} testId="provider-insurance" /></div>
            <p className="w-muted flex items-center gap-1"><MapPin size={16} aria-hidden="true" /> {p.city} · {p.district_plz}</p>
            <p>{p.bio}</p>
            <div className="flex items-center gap-3 pt-1">
              <ReportButton targetType="profile" targetId={p.id} label="Profil melden" testId="provider-report" />
            </div>
          </div>
        </div>
        <Section title="Leistungen & Infos" testId="provider-services-section"><ServicesInfo p={p} /></Section>
        <Section title="Verfügbarkeit">
          <div className="flex gap-2 flex-wrap">{DAYS.map((d, i) => <Chip key={d} tone={p.weekdays.includes(i) ? "mint" : "neutral"}>{p.weekdays.includes(i) ? "✓" : "–"} {d}</Chip>)}</div>
        </Section>
        <Section title="Ungefährer Standort" testId="provider-location-section">
          {p.approx ? (
            <>
              <ProviderMiniMap approx={p.approx} name={p.display_name} />
              <p className="w-muted text-sm mt-3 flex items-start gap-2"><EyeOff size={16} className="shrink-0 mt-0.5" aria-hidden="true" /> Nur die ungefähre Lage im Viertel {p.district_plz} – die genaue Adresse siehst du erst nach deiner Buchung.</p>
            </>
          ) : <p className="w-muted">Standort nicht verfügbar.</p>}
        </Section>
        <Section title={`Bewertungen (${p.reviews.length})`} testId="provider-reviews">
          <p className="w-muted text-sm mb-4" data-testid="reviews-verification-note">Bewertungen stammen ausschließlich von Kundinnen und Kunden, die über Washly einen Auftrag mit diesem Waschhelden abgeschlossen haben.</p>
          {!p.reviews.length ? <p className="w-muted">Noch keine Bewertungen.</p> : (
            <ul className="space-y-4">{p.reviews.map((r, i) => (
              <li key={i} className="border-b border-line pb-4 last:border-0"><div className="flex justify-between"><span className="font-semibold">{r.author_name}</span><Stars value={r.rating} /></div>
                <p className="mt-1">{r.text}</p>
                <div className="flex items-center justify-between gap-2"><p className="w-muted text-sm">{fmtDate(r.created_at, lang)}</p><ReportButton targetType="review" targetId={`${p.id}-${i}`} label="Bewertung melden" testId={`review-report-${i}`} /></div></li>))}</ul>
          )}
        </Section>
      </div>
      <aside className="lg:col-span-4">
        <div className="w-card p-6 lg:sticky lg:top-20 space-y-4">
          <p className="w-muted">Einheitspreis</p>
          <p className="text-4xl font-extrabold tabular-nums">{eur(p.price_per_kg || 2.99)} <span className="text-lg w-muted">{t("search.perkg")}</span></p>
          <button className="w-btn w-btn-primary w-full" onClick={() => nav(`/book/${p.id}`)} data-testid="provider-book-btn">Jetzt buchen</button>
          <button className="w-btn w-btn-secondary w-full mt-2" onClick={() => nav(`/preise?provider=${p.id}`)} data-testid="provider-calc-btn">Preis berechnen</button>
          <p className="w-muted flex gap-2"><EyeOff size={18} className="shrink-0 mt-1" aria-hidden="true" /> {t("book.address_privacy")}</p>
          <p className="w-muted">{t("book.pay_after")}</p>
          <p className="w-muted text-sm border-t border-line pt-3" data-testid="provider-intermediary-note">Washly vermittelt nur. Die Wäscheleistung erbringt {p.display_name} ({p.acts_as === "unternehmer" ? "gewerblicher Anbieter" : "privater Anbieter"}).</p>
        </div>
      </aside>
    </div>
  );
}
